import React, { useRef, useReducer, VFC, Reducer } from 'react';
import { SnackbarProvider } from 'notistack';
import queryString from 'query-string';
import { MomentInput } from 'moment';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import PlanApiService from '../../services/PlanApiService';
import { initialState, reducer, ACTION_TYPE, State, Action } from './reducer';
import AuthDialogContextProvider from '../AuthDialog/AuthDialogContextProvider';
import HeaderComponent from './templates';
import DummyHeader from './atoms/DummyHeader';
import ScheduleSnackbar from './organisms/ScheduleSnackbar';
import SnsBanner from './organisms/SnsBanner';

// js codes
import { BUSINESSES, PLANS, HOME } from '../../routing';
import { APP_VIEW } from '../../constants';
// TODO ファイルの依存関係的に危険な匂いがするので、Snackbarコンポーネントを適切なディレクトリに配置したい
import Snackbar from '../../../../components/front/Snackbar';
import SearchDrawer from './organisms/SearchDrawer';

const SNS_BANNER_DISPLAY_PATHS = [BUSINESSES, PLANS, HOME];
const isAppView = !window.location.pathname.includes(APP_VIEW);
const isDisplayBanner = SNS_BANNER_DISPLAY_PATHS.includes(
  window.location.pathname,
);

const planApiService = new PlanApiService();

export interface Props {
  authenticated?: boolean;
  imageUrl?: string | null;
  isWorking?: boolean;
  message?: string | string[] | null;
}
const Header: VFC<Props> = ({
  authenticated,
  isWorking,
  imageUrl,
  message,
}: Props) => {
  const [state, dispatch] = useReducer<Reducer<State, Action>>(
    reducer,
    initialState,
  );
  // HideOnScroll内のtransitionで使用する
  const headerComponentRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const isPC = useMediaQuery(theme.breakpoints.up('md'));

  // アプリ内ブラウザからのアクセスの場合、ヘッダーは不要なので余計な計算をする前に早期returnする
  if (!isAppView) return null;

  // Event handlers
  const handleDrawer = (event, open) => {
    if (
      event &&
      event.type === 'keydown' &&
      (event.key === 'Tab' || event.key === 'Shift')
    ) {
      return;
    }
    dispatch({
      type: ACTION_TYPE.SET_SP_DRAWER_OPEN,
      payload: { spDrawerOpen: open },
    });
  };
  const handleOpenSPDrawer = (event) => {
    handleDrawer(event, true);
  };
  const handleCloseSPDrawer = (event) => {
    handleDrawer(event, false);
  };
  const handleClickSPDrawer = (event) => {
    state.spDrawerOpen ? handleCloseSPDrawer(event) : handleOpenSPDrawer(event);
  };

  const handleOpenProfileMenu = (anchorEl: Element | null) => {
    dispatch({
      type: ACTION_TYPE.SET_PROFILE_MENU_EL,
      payload: { profileMenuEl: anchorEl },
    });
  };

  const fetchSearchChoice = () => {
    planApiService
      .fetchSearchChoice()
      .then(({ tags, regions }) => {
        dispatch({
          type: ACTION_TYPE.SET_SEARCH_CHOICE,
          payload: { loadedSearchChoice: true, tags, regions },
        });
      })
      .catch((err) => {});
  };
  const handleOpenSearchDrawer = () => {
    dispatch({
      type: ACTION_TYPE.SET_SEARCH_CHOICE,
      payload: { searchDrawerOpen: true, spDrawerOpen: false },
    });

    if (!state.loadedSearchChoice) {
      fetchSearchChoice();
    }
  };
  const handleCloseSearchDrawer = () => {
    dispatch({
      type: ACTION_TYPE.SET_SEARCH_DRAWER_OPEN,
      payload: { searchDrawerOpen: false },
    });
  };

  const handleChangeSearchForm = (event) => {
    const { name, value } = event.target;
    dispatch({
      type: ACTION_TYPE.SET_SEARCH_FORM,
      payload: {
        ...state,
        searchForm: {
          ...state.searchForm,
          [name]: value,
        },
      },
    });
  };
  const handleChangeSearchTerm = (data: {
    startDate: MomentInput;
    endDate: MomentInput;
  }) => {
    dispatch({
      type: ACTION_TYPE.SET_SEARCH_FORM,
      payload: {
        ...state,
        searchForm: {
          ...state.searchForm,
          startDate: data.startDate as string,
          endDate: data.endDate as string,
        },
      },
    });
  };
  const reset = () => {
    dispatch({
      type: ACTION_TYPE.RESET_SEARCH_FORM,
      payload: {},
    });
  };
  const search = () => {
    const presenceState = Object.fromEntries(
      Object.entries(state.searchForm).filter(([_, value]) => !!value),
    );
    const queryParamStr = queryString.stringify(presenceState);

    // locationのsetは string | Location になっていてエラーが起きないはずだが、
    // stringを代入できないというエラーが発生するため、TSのチェックをignoreしている
    // @ts-ignore
    window.location = `${PLANS}?${queryParamStr}`;
  };

  return (
    <SnackbarProvider>
      <AuthDialogContextProvider isLoggedIn={!!authenticated}>
        <HeaderComponent
          {...state}
          ref={headerComponentRef}
          isPC={isPC}
          authenticated={!!authenticated}
          imageUrl={imageUrl}
          handleOpenSPDrawer={handleOpenSPDrawer}
          handleCloseSPDrawer={handleCloseSPDrawer}
          handleClickSPDrawer={handleClickSPDrawer}
          handleOpenSearchDrawer={handleOpenSearchDrawer}
          handleOpenProfileMenu={handleOpenProfileMenu}
        />
        <DummyHeader isPC={isPC} />
        <SearchDrawer
          open={state.searchDrawerOpen}
          searchForm={state.searchForm}
          regions={state.regions}
          tags={state.tags}
          onClose={handleCloseSearchDrawer}
          handleChangeSearchForm={handleChangeSearchForm}
          handleChangeSearchTerm={handleChangeSearchTerm}
          reset={reset}
          search={search}
        />
        {isDisplayBanner && !authenticated && <SnsBanner />}
        {isWorking && <ScheduleSnackbar />}
        <Snackbar message={message} options={{ variant: 'success' }} />
      </AuthDialogContextProvider>
    </SnackbarProvider>
  );
};

export default Header;
